import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Sparkles } from 'lucide-react';

/**
 * TickerTape — site-wide attention-grabbing scrolling banner.
 *
 * Sits above the nav on every marketing page (never on /admin or /portal).
 * Uses a CSS keyframe animation, pauses on hover, and is dismissible for
 * the session via localStorage so returning visitors aren't nagged.
 *
 * Content is a mix of real client outcomes, offer specifics and one
 * availability nudge. Update the MESSAGES array to change the ticker.
 * No fake numbers — every item here maps to something on /results or
 * something Jo has confirmed.
 */

// Content sources:
//   - Juliette: her result on /results (launched inside 1 month, top 10%
//     worldwide inside 2 months). Not quoted, stated as an outcome.
//   - Katie Bell: PortfolioGrid tile ("200+ episodes... 500+ clinic
//     owners worldwide"). Stated as an outcome, not quoted.
//   - Lucy: not included in the ticker to avoid abbreviating a real quote.
//   - Availability, price, cancel policy: Jo-confirmed offer terms.
// Only add new items whose facts are traceable to a source in the repo
// or to a Jo confirmation.
const MESSAGES = [
  '\u2b50  Juliette Karaman launched inside 1 month, top 10% worldwide in 2',
  '\u2b50  Katie Bell (Thrive Business Coaching): 200+ episodes shipped, 500+ clinic owners reached',
  '\ud83d\udcb7  \u00a3280/week, cancel anytime after week 4',
  '\ud83c\udf99\ufe0f  Record once a week. We ship the podcast, YouTube, Shorts, blog and email',
];

const DISMISS_KEY = 'ticker_tape_dismissed_v1';

export default function TickerTape() {
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(DISMISS_KEY) === '1');
    } catch (_) {}
  }, []);

  // Hide on the admin panel and portal — those are apps, not marketing.
  if (pathname.startsWith('/admin') || pathname.startsWith('/portal')) return null;
  if (dismissed) return null;

  const handleDismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (_) {}
    setDismissed(true);
  };

  // Duplicate the message list so the animation loops seamlessly.
  const items = [...MESSAGES, ...MESSAGES];

  return (
    <div
      className="relative w-full max-w-full bg-[#1E2A44] text-white text-sm overflow-hidden border-b border-[#BC516F]/40 group"
      data-testid="ticker-tape"
      role="region"
      aria-label="Announcements"
    >
      <div className="flex items-center min-w-0">
        <span
          className="hidden sm:inline-flex items-center gap-1.5 flex-shrink-0 pl-4 pr-3 py-2 bg-[#BC516F] text-white font-display font-semibold text-xs uppercase tracking-widest"
          aria-hidden="true"
        >
          <Sparkles className="w-3.5 h-3.5" />
          Live
        </span>

        <div className="ticker-viewport flex-1 min-w-0 overflow-hidden py-2 relative">
          <div
            className="ticker-track inline-flex whitespace-nowrap"
            data-testid="ticker-track"
          >
            {items.map((msg, i) => (
              <Link
                key={i}
                to="/book"
                className="inline-flex items-center gap-3 px-6 hover:text-[#BC516F] transition-colors"
                data-testid={`ticker-item-${i}`}
              >
                <span className="font-medium">{msg}</span>
                <span className="text-white/40">·</span>
              </Link>
            ))}
          </div>
        </div>

        <button
          onClick={handleDismiss}
          aria-label="Dismiss announcements"
          className="flex-shrink-0 px-3 py-2 text-white/60 hover:text-white transition-colors"
          data-testid="ticker-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <style>{`
        .ticker-viewport { mask-image: linear-gradient(to right, transparent, black 40px, black calc(100% - 40px), transparent); }
        .ticker-track { animation: ticker-scroll 45s linear infinite; }
        .group:hover .ticker-track { animation-play-state: paused; }
        @keyframes ticker-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .ticker-track { animation-duration: 120s; }
        }
      `}</style>
    </div>
  );
}
